import React from "react";
import PolicyLayout from "../../components/common/PolicyLayout";
import { X } from "../../components/common/Placeholder";

export default function FairPracticesCode() {
  return (
    <PolicyLayout title="Fair Practices Code" lastUpdated="03 Jul 2026">
      <h2>Background</h2>
      <p>
        This Fair Practices Code (“FPC” or “Code”) has been formulated in accordance
        with the guidelines issued by the Reserve Bank of India (“RBI”) for
        Non-Banking Financial Companies (“NBFCs”). The Code reflects the Company's
        commitment towards transparency, ethical business practices, fair lending
        principles, and responsible customer service. The purpose of this Code is to
        provide borrowers and stakeholders with a clear understanding of the practices
        and procedures followed by the Company while dealing with customers in all
        aspects of its lending operations. This Code shall apply to all products and
        services offered by the Company through physical branches, digital platforms,
        websites, telecommunication channels, or any other mode of interaction.
      </p>

      <h2>Objectives of the Fair Practices Code</h2>
      <p>The key objectives of this Code are to:</p>
      <ul>
        <li>Promote fair, transparent, and ethical business practices in all customer dealings</li>
        <li>Ensure responsible lending and customer-centric service delivery</li>
        <li>Provide customers with complete and accurate information to facilitate informed decisions</li>
        <li>Ensure compliance with applicable laws, RBI guidelines, and regulatory requirements</li>
        <li>Establish an efficient grievance redressal mechanism</li>
        <li>Promote professionalism, accountability, and fair recovery practices</li>
      </ul>

      <h2>1. Loan Applications and Processing</h2>
      <p>
        The Company shall provide applicants with all necessary information relating
        to loan products and services to enable meaningful comparison and informed
        decision-making.
      </p>
      <h3>Loan Application Forms</h3>
      <p>Loan application forms shall clearly indicate:</p>
      <ul>
        <li>Information required from the borrower</li>
        <li>Documents to be submitted along with the application</li>
        <li>Applicable terms, conditions, and charges</li>
        <li>Processing timelines, wherever applicable</li>
      </ul>
      <p>
        The Company shall acknowledge receipt of all loan applications through
        appropriate modes. If additional information or documents are required, the
        borrower shall be informed appropriately. In case a loan application is
        rejected, the Company may communicate the reason(s) for such rejection,
        subject to applicable laws and internal policies.
      </p>

      <h2>2. Loan Appraisal and Terms &amp; Conditions</h2>
      <p>
        After evaluating the loan application, the Company shall communicate the
        sanction decision to the borrower in writing through a sanction letter or
        similar communication. The sanction communication shall include:
      </p>
      <ul>
        <li>Loan amount sanctioned</li>
        <li>Rate of interest and method of application</li>
        <li>Repayment terms and tenure</li>
        <li>Applicable charges and fees</li>
        <li>Penal charges, if any</li>
        <li>Other material terms and conditions</li>
      </ul>
      <p>
        The Company shall provide the borrower with a copy of the executed loan
        agreement and relevant documents at the time of loan disbursement. All
        communications shall be made in English or in a language understood by the
        borrower.
      </p>

      <h2>3. Disbursement of Loans and Changes in Terms</h2>
      <p>
        The Company shall disburse loans in accordance with the terms and conditions
        agreed with the borrower. Any change in:
      </p>
      <ul>
        <li>Interest rates</li>
        <li>Service charges</li>
        <li>Repayment schedules</li>
        <li>Prepayment terms</li>
        <li>Other material conditions</li>
      </ul>
      <p>
        shall be communicated to the borrower in advance and shall apply prospectively.
        Any decision regarding recall, acceleration, or enforcement of security shall
        be carried out strictly in accordance with the terms of the loan agreement and
        applicable law.
      </p>

      <h2>4. Interest Rates and Charges</h2>
      <p>
        The Company shall adopt appropriate internal policies and procedures for
        determining interest rates, processing fees, and other charges. Interest rates
        shall be determined considering factors such as:
      </p>
      <ul>
        <li>Cost of funds</li>
        <li>Operational expenses</li>
        <li>Risk profile of the borrower</li>
        <li>Market conditions</li>
        <li>Business strategy and regulatory requirements</li>
      </ul>
      <p>
        The rate of interest and approach for risk gradation shall be made available
        on the Company's website, wherever applicable. All interest rates shall be
        communicated in annualized terms for transparency and customer understanding.
      </p>

      <h2>5. Responsible Lending and Recovery Practices</h2>
      <p>
        The Company believes in responsible lending practices and shall assess the
        repayment capacity of borrowers before extending financial assistance. The
        Company and its representatives shall:
      </p>
      <ul>
        <li>Treat borrowers with dignity and respect</li>
        <li>Avoid harassment, intimidation, or coercive recovery methods</li>
        <li>Refrain from contacting borrowers at inappropriate hours</li>
        <li>Follow ethical, lawful, and fair recovery practices at all times</li>
      </ul>
      <p>
        Recovery proceedings, if required, shall be conducted strictly in accordance
        with applicable laws and the terms of the loan agreement.
      </p>

      <h2>6. Security and Repossession</h2>
      <p>
        Where loans are secured, the loan agreement shall contain appropriate
        provisions relating to repossession or enforcement of security. The
        repossession process shall include:
      </p>
      <ul>
        <li>Adequate notice to the borrower</li>
        <li>Circumstances under which notice may be waived</li>
        <li>Procedure for taking possession of security</li>
        <li>Opportunity for repayment before sale or auction</li>
        <li>Transparent process for sale or disposal of security assets</li>
      </ul>
      <p>
        Upon full repayment of all dues, the Company shall release securities subject
        to any lawful lien, right of set-off, or other legitimate claim.
      </p>

      <h2>7. Non-Discrimination Policy</h2>
      <p>
        The Company shall not discriminate against any borrower on the basis of
        religion, caste, gender, marital status, or other prohibited grounds while
        considering loan applications. However, the Company may participate in special
        schemes or programs designed for specific sections of society in accordance
        with applicable laws or government initiatives.
      </p>

      <h2>8. Grievance Redressal Mechanism</h2>
      <p>
        The Company has established an efficient Grievance Redressal Mechanism to
        address customer complaints and concerns in a fair and timely manner.
        Customers may contact the Company through the prescribed communication
        channels for resolution of grievances relating to products, services, staff
        behavior, or operational issues. All grievances shall be reviewed and resolved
        within a reasonable timeframe.
      </p>

      <h2>9. Confidentiality</h2>
      <p>
        All customer information and data shall be treated as confidential and shall
        not be disclosed to third parties except:
      </p>
      <ul>
        <li>Where disclosure is required by law</li>
        <li>Where disclosure is permitted by the borrower</li>
        <li>Where disclosure is necessary for credit reporting, legal proceedings, audits, or regulatory compliance</li>
      </ul>

      <h2>10. Force Majeure</h2>
      <p>
        The commitments under this Code are applicable under normal operating
        conditions. The Company shall not be held responsible for delay or inability
        to perform obligations arising due to circumstances beyond its reasonable
        control, including natural disasters, technical failures, regulatory
        restrictions, or other force majeure events.
      </p>

      <h2>11. Periodic Review</h2>
      <p>
        This Fair Practices Code shall be reviewed periodically by the Company's
        management and Board of Directors to ensure compliance with RBI guidelines,
        regulatory updates, and evolving business practices. The latest version of
        the Code shall be made available on the Company's website for public access.
      </p>
    </PolicyLayout>
  );
}
